import React from "react";
import { Box, Typography } from "@material-ui/core";
import { resolverTextoConVariables, resolverValorAtributo } from "./attributeLogic";
import { distribuirEnFilas, LOGO_SENTINEL } from "./gridLogic";
import logoBase64 from "./logoDashport";

// Qué tan grande se ve la etiqueta en pantalla — el PDF real usa milímetros,
// aquí solo se escala para que sea legible en el editor.
const PX_POR_MM = 4;
// jsPDF setFontSize usa puntos; se convierte a mm y luego a px con la misma
// escala, para que el tamaño relativo del texto se vea parecido al PDF.
const PT_A_MM = 0.3528;

function CeldaPreview({ cell, participante, contexto }) {
  const esImagen = cell.content?.type === "image";
  const esVertical = cell.orientation === "vertical";

  const estiloContenedor = {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    boxSizing: "border-box",
    padding: 2,
    border: "1px dashed #e0e0e0",
  };

  if (esImagen) {
    const src = cell.content.src === LOGO_SENTINEL ? logoBase64 : cell.content.src;
    return (
      <Box style={estiloContenedor}>
        {src && (
          <img
            src={src}
            alt=""
            style={{
              maxWidth: esVertical ? "70%" : "90%",
              maxHeight: esVertical ? "70%" : "90%",
              transform: esVertical ? "rotate(-90deg)" : undefined,
            }}
          />
        )}
      </Box>
    );
  }

  const texto = resolverTextoConVariables(cell.content?.value, (key) =>
    resolverValorAtributo(key, participante, contexto.atributosPorKey, contexto.eventSubEventsRegistry, contexto.valueRemap)
  );
  const fontSizePx = (cell.fontSize || 9) * PT_A_MM * PX_POR_MM;

  return (
    <Box style={estiloContenedor}>
      <span
        style={{
          fontSize: fontSizePx,
          fontFamily: "Arial, sans-serif",
          lineHeight: 1.1,
          textAlign: "center",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          transform: esVertical ? "rotate(-90deg)" : undefined,
          display: "inline-block",
        }}
      >
        {texto}
      </span>
    </Box>
  );
}

function ColumnaPreview({ columna, participante, contexto }) {
  const filasDeCeldas = distribuirEnFilas(columna.cells, (c) => c.width);
  return (
    <Box style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      {filasDeCeldas.map((filaCeldas, i) => {
        const totalWidth = filaCeldas.reduce((acc, c) => acc + (c.width || 12), 0) || 12;
        return (
          <Box
            key={i}
            style={{ display: "flex", flex: `1 1 ${100 / filasDeCeldas.length}%`, minHeight: 0 }}
          >
            {filaCeldas.map((cell) => (
              <Box key={cell.id} style={{ width: `${((cell.width || 12) / totalWidth) * 100}%` }}>
                <CeldaPreview cell={cell} participante={participante} contexto={contexto} />
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

// Vista previa aproximada de la etiqueta a escala, con datos de un
// participante de ejemplo. Usa la misma función de acomodo (distribuirEnFilas)
// que el generador del PDF real, así que el salto de línea se ve igual —
// no es pixel-perfect (la fuente se ve distinta en el navegador que en el
// PDF), pero sirve para juzgar proporciones y acomodo sin tener que generar
// el PDF cada vez.
export default function LabelPreview({
  grid,
  sheetFormat,
  participante,
  atributosDisponibles,
  eventSubEventsRegistry,
  valueRemap,
}) {
  const atributosPorKey = {};
  (atributosDisponibles || []).forEach((a) => {
    atributosPorKey[a.key] = a;
  });
  const contexto = { atributosPorKey, eventSubEventsRegistry, valueRemap };

  const anchoPx = (sheetFormat.etiquetaAncho || 67) * PX_POR_MM;
  const altoPx = (sheetFormat.etiquetaAlto || 25) * PX_POR_MM;
  const filasDeColumnas = distribuirEnFilas(grid.columns, (c) => c.width);

  return (
    <Box>
      <Typography variant="caption" style={{ display: "block", marginBottom: 4, opacity: 0.7 }}>
        Vista previa aproximada
        {participante?.nombres ? ` (con ${participante.nombres} ${participante.apellidos})` : ""} — no
        reemplaza generar el PDF antes de imprimir.
      </Typography>
      <Box
        style={{
          width: anchoPx,
          height: altoPx,
          border: "1px solid #999",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "#fff",
        }}
      >
        {filasDeColumnas.map((filaColumnas, i) => {
          const totalWidth = filaColumnas.reduce((acc, c) => acc + (c.width || 12), 0) || 12;
          return (
            <Box
              key={i}
              style={{ display: "flex", flex: `1 1 ${100 / filasDeColumnas.length}%`, minHeight: 0 }}
            >
              {filaColumnas.map((columna) => (
                <Box key={columna.id} style={{ width: `${((columna.width || 12) / totalWidth) * 100}%` }}>
                  <ColumnaPreview columna={columna} participante={participante} contexto={contexto} />
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
